import type { Doc } from "building-types/doc";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import { polyLine2Edge, polyLine2Vertex } from "geom/polyLine2";
import { docGetCatalogItem } from "../docGetCatalogItem";
import { docGetContentTemplate } from "../docGetContentTemplates";
import { docGetElectricalRule } from "../docGetElectricalRules";
import { docGetPreset } from "../docGetPresets";
import { docGetRoofEdgeOverrides } from "../docGetRoofs";
import { docGetShape } from "../docGetShape";
import {
  getFloorIdForBay,
  getFloorIdForSplitLine,
  getFloorSplitData,
  isHorizontalFloorAssemblyRoomDerived,
  isHorizontalFloorAssemblyWithOpenings,
  isSolidFloorAssemblyOverride,
} from "../elements/floor/floorAssembly";
import { docGetOption } from "../options/docGetOptionSets";
import type { DocEntity } from "./docEntity";
import { isFloorEdgeHandle, isFloorOpeningEdgeHandle } from "./docEntityEdgeHandleTools";
import { docEntityHandleIsTableBased } from "./docEntityHandleIsTableBased";
import { docGetDataSequence } from "./docGetDataSequence";
import { docGetEntityHandleById } from "./docGetEntityHandleById";
import { docGetGroup } from "./docGetGroup";
import { docGetSystemParameter } from "./docGetParameter";
import { docGetPropAttributes } from "./docGetProps";
import { docGetTableEntity } from "./docGetTableEntity";

/**
 * Get a doc entity by its handle. This returns a DocEntity, which is a sum type. Hence, it
 * isn't useful when you know the type of the entity you're getting. In those cases,
 * use docGetTableEntity or some of the other type specific variants (docGetFoundationPattern)
 * that are not stored as table entities.
 *
 * It's more common to use `docGetTableEntity` for entities that are stored in tables
 * (e.g. `docGetTableEntity(doc, { type: "LEVEL", id: "1" })`). There are a few entities
 * that are not stored in tables.
 *
 * TODO: this function can actually return undefined. We should change the return type to DocEntity | undefined and update all callers.
 */
export default function docGetEntity(doc: Doc, handle: DocEntityHandle | undefined): DocEntity | null {
  if (!handle) {
    return null;
  }

  switch (handle.type) {
    case "PROP":
      return docGetPropAttributes(doc, handle);
    case "ELECTRICAL_RULE":
      return docGetElectricalRule(doc, handle);
    case "PRESET":
      return docGetPreset(doc, handle) ?? null;
    case "SHAPE":
      return docGetShape(doc, handle) ?? null;
    case "EDGE": {
      if (isFloorOpeningEdgeHandle(handle)) {
        const opening = docGetShape(doc, {
          type: "SHAPE",
          parent: handle.parent,
          shapeId: `opening:${handle.openingIndex}`,
        });
        if (!opening) {
          return null;
        }
        return polyLine2Edge(opening, 0);
      }
      if (isFloorEdgeHandle(handle)) {
        const perimeter = docGetShape(doc, {
          type: "SHAPE",
          parent: handle.parent,
          shapeId: `outline:${handle.perimeterIndex}`,
        });
        if (!perimeter) {
          return null;
        }
        return polyLine2Edge(perimeter, handle.edgeId);
      }
      const path = docGetShape(doc, { ...handle, type: "SHAPE" });
      if (!path) {
        return null;
      }
      return polyLine2Edge(path, handle.edgeId);
    }
    case "VERTEX": {
      const path = docGetShape(doc, { ...handle, type: "SHAPE" });
      if (!path) {
        return null;
      }
      return polyLine2Vertex(path, handle.vertexId);
    }
    case "ROOF_SIDE":
      return docGetRoofEdgeOverrides(doc, handle);
    case "CATALOG_ITEM":
      return docGetCatalogItem(doc, handle) ?? null;
    case "OPTION":
      return docGetOption(doc, handle);
    case "EDIT_MODE":
      return { entityType: "EDIT_MODE_PLACEHOLDER_ENTITY" };
    case "AUTO_DIMENSION":
      return { entityType: "AUTO_DIMENSION_PLACEHOLDER_ENTITY" };
    case "GENERATED_ENTITY":
      return { entityType: "GENERATED_PLACEHOLDER_ENTITY" };
    case "FLOOR_BAY": {
      const bayFloorId = getFloorIdForBay(doc.floors, handle.id);
      if (!bayFloorId) {
        return null;
      }
      return { entityType: "FLOOR_BAY_PLACEHOLDER_ENTITY" };
    }
    case "FLOOR_SPLIT_LINE": {
      const slFloorId = getFloorIdForSplitLine(doc.floors, handle.id);
      const slFloor = slFloorId ? doc.floors?.[slFloorId] : undefined;
      const slSplitData = slFloor ? getFloorSplitData(slFloor) : undefined;
      return slSplitData?.splitLines?.[handle.id] ? { entityType: "FLOOR_SPLIT_LINE_PLACEHOLDER_ENTITY" } : null;
    }
    case "FLOOR_PERIMETER_OPENING": {
      const floor = doc.floors?.[handle.floorId];
      if (!floor || !isSolidFloorAssemblyOverride(floor)) {
        return null;
      }
      if (isHorizontalFloorAssemblyWithOpenings(floor)) {
        const openings = floor.openings ?? [];
        if (openings[handle.openingIndex] === undefined) {
          return null;
        }
        return { entityType: "FLOOR_PERIMETER_OPENING_PLACEHOLDER_ENTITY" };
      }
      if (isHorizontalFloorAssemblyRoomDerived(floor)) {
        return { entityType: "FLOOR_PERIMETER_OPENING_PLACEHOLDER_ENTITY" };
      }
      return null;
    }
    case "SYSTEM_PARAMETER":
      return docGetSystemParameter(doc, handle) ?? null;
    case "INDETERMINATE_ENTITY_TYPE":
      const resolvedHandle = docGetEntityHandleById(doc, handle.id, handle.entityTypes);
      if (!resolvedHandle || resolvedHandle.type === "INDETERMINATE_ENTITY_TYPE") {
        return null;
      }
      return docGetEntity(doc, resolvedHandle);
    case "GROUP":
      return docGetGroup(doc, handle) ?? null;
    case "DATA_SEQUENCE":
      return docGetDataSequence(doc, handle);
    case "CONTENT_TEMPLATE":
      return docGetContentTemplate(doc, handle) ?? null;
    default: {
      if (docEntityHandleIsTableBased(handle)) {
        return docGetTableEntity(doc, handle);
      }
    }
  }

  return null;
}
